import { useCallback, useRef } from "react";

import { useAuth } from "../contexts/AuthContext";
import { useUpdateUserPreferences } from "../api/userPreferences";
import { UiFlagKey, UiFlagValue } from "../types/auth";

export interface UseUiFlagResult {
  /** Current value, or null when the flag has never been set. */
  value: UiFlagValue;
  /** True while the user record is still loading — don't act on `value` yet. */
  loading: boolean;
  /** Persist a value. Optimistic locally; failures are non-fatal (see below). */
  set: (value: UiFlagValue) => void;
}

/**
 * Read and write one key of `User.ui_flags`.
 *
 * Server-side rather than localStorage: "I've seen this" should follow the
 * person, not the browser profile — a flag in localStorage re-fires the tour
 * on every new machine, which reads as the app being broken.
 *
 * Writes are optimistic and their failures are swallowed. The worst outcome of
 * a lost write is that a one-off hint appears a second time, which is not worth
 * an error toast over the top of the thing it was explaining.
 */
export function useUiFlag(key: UiFlagKey): UseUiFlagResult {
  const { user, loading, updateUser } = useAuth();
  const updatePreferences = useUpdateUserPreferences();

  const value = user?.ui_flags?.[key] ?? null;

  // The existing flags are read through a ref so `set` keeps a stable identity.
  // Callers put `set` in effect deps; if it changed on every auth refresh, a
  // timer-based caller (the coach tour) would reset its timer forever and never
  // fire.
  const flagsRef = useRef(user?.ui_flags);
  flagsRef.current = user?.ui_flags;

  const set = useCallback(
    (next: UiFlagValue) => {
      // Update in place first so the caller's effect doesn't re-fire while the
      // PATCH is in flight (the tour would restart under the user's cursor).
      updateUser({ ui_flags: { ...(flagsRef.current ?? {}), [key]: next } });
      updatePreferences.mutate(
        { ui_flags: { [key]: next } },
        { onError: () => undefined }
      );
    },
    // react-query's `mutate` is stable, and `updateUser` is a fresh closure per
    // render but only ever calls the functional form of setUser — so capturing
    // the first one is safe rather than stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [key]
  );

  return { value, loading, set };
}
